import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Edit, FileText, DollarSign, TrendingUp, ClipboardList,
  Building2, MapPin, Phone, Mail, User, CreditCard, Calendar,
  Clock, Package, CheckCircle, Send, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  PENDENTE:   { label: 'Pendente',   cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  IMPLANTADO: { label: 'Implantado', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  NF_EMITIDA: { label: 'NF Emitida', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELADO:  { label: 'Cancelado',  cls: 'bg-slate-100 text-slate-500 border-slate-300' },
  ATRASADO:   { label: 'Atrasado',   cls: 'bg-red-100 text-red-700 border-red-300' },
};

const SAUDE_CONFIG = {
  ativo:           { label: 'Ativo',           cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  inativo_recente: { label: 'Inativo Recente', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  inativo_antigo:  { label: 'Inativo Antigo',  cls: 'bg-red-100 text-red-700 border-red-300' },
  prospect:        { label: 'Prospect',         cls: 'bg-slate-100 text-slate-500 border-slate-300' },
};

const SEG_COLORS = {
  Supermercado: 'bg-blue-100 text-blue-700 border-blue-200',
  Indústria:    'bg-amber-100 text-amber-700 border-amber-200',
  Atacado:      'bg-purple-100 text-purple-700 border-purple-200',
  Varejo:       'bg-emerald-100 text-emerald-700 border-emerald-200',
  Distribuidor: 'bg-teal-100 text-teal-700 border-teal-200',
  Outros:       'bg-slate-100 text-slate-600 border-slate-200',
};

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => { if (!d) return '---'; return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { timeZone: 'UTC' }); };
const fmtDateTime = (d) => {
  if (!d) return '---';
  const dt = new Date(d);
  if (isNaN(dt)) return '---';
  return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const maskCNPJ = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
};

const maskTelefone = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 11);
  if (d.length === 0)  return '';
  if (d.length <= 2)   return `(${d}`;
  if (d.length <= 6)   return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10)  return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`;
};

const maskCEP = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0,5)}-${d.slice(5)}`;
};

const classifySaude = (pedidos) => {
  if (!pedidos || pedidos.length === 0) return 'prospect';
  let maxTs = 0;
  pedidos.forEach(p => {
    let t = 0;
    if (p.data_entrega) t = new Date(p.data_entrega + 'T00:00:00').getTime();
    else if (p.criado_em) t = new Date(p.criado_em).getTime();
    if (!isNaN(t) && t > maxTs) maxTs = t;
  });
  if (maxTs === 0) return 'prospect';
  const days = Math.floor((Date.now() - maxTs) / 86400000);
  if (days <= 30)  return 'ativo';
  if (days <= 90)  return 'inativo_recente';
  return 'inativo_antigo';
};

const ClienteDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [cliente, setCliente] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [orcamentos, setOrcamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rCliente, rPedidos, rOrcs] = await Promise.all([
        api.get(`/clientes/${id}`),
        api.get('/pedidos'),
        api.get('/orcamentos').catch(() => ({ data: [] })),
      ]);
      const c = rCliente.data;
      setCliente(c);

      const filtrados = (Array.isArray(rPedidos.data) ? rPedidos.data : []).filter(p =>
        (c.id && p.cliente_id === c.id) ||
        (p.cliente_nome && c.nome &&
          p.cliente_nome.trim().toUpperCase() === c.nome.trim().toUpperCase())
      );
      filtrados.sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em));
      setPedidos(filtrados);

      const orcsDoCliente = (Array.isArray(rOrcs.data) ? rOrcs.data : []).filter(o =>
        (c.id && o.cliente_id === c.id) ||
        (o.cliente_nome && c.nome &&
          o.cliente_nome.trim().toUpperCase() === c.nome.trim().toUpperCase())
      );
      setOrcamentos(orcsDoCliente);
    } catch {
      toast.error('Erro ao carregar cliente');
      navigate('/clientes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Cliente...</p>
    </div>
  );

  if (!cliente) return null;

  const totalPedidos  = pedidos.length;
  const totalValor    = pedidos.reduce((acc, p) => acc + (p.valor_total    || 0), 0);
  const totalComissao = pedidos.reduce((acc, p) => acc + (p.comissao_valor || 0), 0);
  const ultimoPedido  = pedidos.length > 0
    ? fmtDate(pedidos[0].criado_em?.slice(0, 10))
    : '---';

  const saude = SAUDE_CONFIG[classifySaude(pedidos)];

  const buildTimeline = () => {
    const events = [];

    pedidos.forEach(p => {
      if (p.criado_em) {
        events.push({
          id: `ped-create-${p.id}`,
          title: 'Pedido criado',
          detail: `${p.numero_fabrica || p.numero_oc || '---'} · ${brl(p.valor_total)}`,
          date: p.criado_em,
          link: `/pedidos/${p.id}`,
          Icon: Package,
          color: '#1E40AF',
          bg: '#EFF6FF',
        });
      }
      if (p.status === 'NF_EMITIDA') {
        events.push({
          id: `ped-nf-${p.id}`,
          title: 'NF Emitida',
          detail: `${p.numero_fabrica || p.numero_fe || '---'} · ${brl(p.valor_total)}`,
          date: p.atualizado_em || p.criado_em,
          link: `/pedidos/${p.id}`,
          Icon: CheckCircle,
          color: '#059669',
          bg: '#DCFCE7',
        });
      }
    });

    orcamentos.forEach(o => {
      if (o.criado_em) {
        events.push({
          id: `orc-create-${o.id}`,
          title: 'Orçamento criado',
          detail: `${o.numero_proposta || '---'} · ${brl(o.valor_total)}`,
          date: o.criado_em,
          link: `/orcamentos/${o.id}`,
          Icon: FileText,
          color: '#7C3AED',
          bg: '#F3E8FF',
        });
      }
      if (o.status === 'ENVIADO' && o.atualizado_em) {
        events.push({
          id: `orc-send-${o.id}`,
          title: 'Orçamento enviado',
          detail: o.numero_proposta || '---',
          date: o.atualizado_em,
          link: `/orcamentos/${o.id}`,
          Icon: Send,
          color: '#7C3AED',
          bg: '#F3E8FF',
        });
      }
      if ((o.status === 'CONVERTIDO' || o.status === 'APROVADO') && o.atualizado_em) {
        events.push({
          id: `orc-conv-${o.id}`,
          title: 'Orçamento convertido',
          detail: o.numero_proposta || '---',
          date: o.atualizado_em,
          link: `/orcamentos/${o.id}`,
          Icon: ArrowRight,
          color: '#059669',
          bg: '#DCFCE7',
        });
      }
    });

    events.sort((a, b) => new Date(b.date) - new Date(a.date));
    return events.slice(0, 20);
  };

  const timelineEvents = buildTimeline();

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clientes')}
            className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
            <ArrowLeft size={14} /> Voltar
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-black text-slate-900">
                {cliente.nome_fantasia || cliente.nome}
              </h1>
              {cliente.segmento && (
                <span className={`px-2 py-0.5 text-[10px] font-black border rounded-full ${SEG_COLORS[cliente.segmento] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  {cliente.segmento}
                </span>
              )}
              <span className={`px-2 py-0.5 text-[10px] font-black border rounded-full ${saude.cls}`}>
                {saude.label}
              </span>
            </div>
            {cliente.nome_fantasia && (
              <p className="text-xs text-slate-400 font-semibold mt-0.5 uppercase">{cliente.nome}</p>
            )}
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => navigate(`/clientes/editar/${id}`)}
            className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
            <Edit size={13} /> Editar Cadastro
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total de Pedidos', value: totalPedidos,       icon: FileText,   cor: '#1E40AF', fundo: '#EFF6FF' },
          { label: 'Valor Acumulado',  value: brl(totalValor),    icon: DollarSign, cor: '#166534', fundo: '#DCFCE7' },
          { label: 'Comissão Total',   value: brl(totalComissao), icon: TrendingUp, cor: '#92400E', fundo: '#FEF3C7' },
          { label: 'Último Pedido',    value: ultimoPedido,       icon: Calendar,   cor: '#6B21A8', fundo: '#F3E8FF' },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: k.fundo }}>
                <k.icon size={13} style={{ color: k.cor }} />
              </div>
            </div>
            <p className="text-lg font-black" style={{ color: k.cor }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* CORPO 2 COLUNAS */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-6">

        {/* COL ESQUERDA */}
        <div className="space-y-6">

          {/* Card Dados Cadastrais */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4 flex items-center gap-2">
              <Building2 size={13} /> Dados Cadastrais
            </p>
            <div className="space-y-3">
              {cliente.cnpj && (
                <div className="flex items-start gap-2.5">
                  <CreditCard size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={lbl}>CNPJ</p>
                    <p className="text-sm font-black font-mono text-slate-700">{maskCNPJ(cliente.cnpj)}</p>
                  </div>
                </div>
              )}
              {cliente.inscricao_estadual && (
                <div>
                  <p className={lbl}>Inscrição Estadual</p>
                  <p className="text-sm font-bold text-slate-700">{cliente.inscricao_estadual}</p>
                </div>
              )}
              {cliente.comprador && (
                <div className="flex items-start gap-2.5">
                  <User size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={lbl}>Comprador</p>
                    <p className="text-sm font-bold text-slate-700 uppercase">{cliente.comprador}</p>
                  </div>
                </div>
              )}
              {(cliente.cidade || cliente.endereco) && (
                <div className="flex items-start gap-2.5">
                  <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={lbl}>Localização</p>
                    <p className="text-sm font-bold text-slate-700 uppercase">
                      {cliente.cidade}{cliente.estado ? ` — ${cliente.estado}` : ''}
                    </p>
                    {cliente.endereco && <p className="text-xs text-slate-400 font-semibold mt-0.5">{cliente.endereco}</p>}
                    {cliente.cep && <p className="text-xs text-slate-400 font-semibold">CEP: {maskCEP(cliente.cep)}</p>}
                  </div>
                </div>
              )}
              {cliente.email && (
                <div className="flex items-start gap-2.5">
                  <Mail size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={lbl}>E-mail</p>
                    <p className="text-sm font-bold text-slate-700">{cliente.email}</p>
                  </div>
                </div>
              )}
              {cliente.telefone && (
                <div className="flex items-start gap-2.5">
                  <Phone size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className={lbl}>Telefone</p>
                    <p className="text-sm font-bold text-slate-700">{maskTelefone(cliente.telefone)}</p>
                  </div>
                </div>
              )}
              {cliente.isento_ipi && (
                <div>
                  <p className={lbl}>Regime IPI</p>
                  <span className="inline-block px-2 py-0.5 text-[10px] font-black border rounded-full bg-emerald-100 text-emerald-700 border-emerald-300">
                    Isento IPI
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Card Condições Comerciais */}
          {(cliente.prazo_pagamento || cliente.observacoes) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Condições Comerciais</p>
              {cliente.prazo_pagamento && (
                <div className="mb-3">
                  <p className={lbl}>Prazo de Pagamento</p>
                  <p className="text-sm font-bold text-slate-700">{cliente.prazo_pagamento}</p>
                </div>
              )}
              {cliente.observacoes && (
                <div>
                  <p className={lbl}>Observações</p>
                  <p className="text-xs font-semibold text-slate-600 whitespace-pre-wrap">{cliente.observacoes}</p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* COL DIREITA */}
        <div className="space-y-6">

          {/* LINHA DO TEMPO */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Clock size={13} className="text-[#0A3D73]" />
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73]">Linha do Tempo</p>
              <span className="ml-auto text-[10px] font-black text-white bg-[#0A3D73] rounded-full px-2 py-0.5">
                {timelineEvents.length}
              </span>
            </div>

            {timelineEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-3">
                <Clock size={32} className="opacity-20" />
                <p className="text-xs font-black uppercase tracking-widest">Nenhuma atividade registrada</p>
              </div>
            ) : (
              <div className="px-5 py-4 max-h-[460px] overflow-y-auto">
                {timelineEvents.map((ev, idx) => {
                  const isLast = idx === timelineEvents.length - 1;
                  return (
                    <div key={ev.id} className="flex gap-3">
                      {/* círculo + linha conectora */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: ev.bg, border: `2px solid ${ev.color}` }}>
                          <ev.Icon size={12} style={{ color: ev.color }} strokeWidth={2} />
                        </div>
                        {!isLast && (
                          <div className="w-px flex-1 mt-1 mb-1 min-h-[20px]" style={{ background: '#0A3D73', opacity: 0.15 }} />
                        )}
                      </div>

                      {/* conteúdo do evento */}
                      <button
                        onClick={() => navigate(ev.link)}
                        className={`flex-1 text-left hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors ${isLast ? 'pb-1' : 'pb-4'}`}>
                        <div className="flex items-start justify-between gap-2 pt-0.5">
                          <div className="min-w-0">
                            <p className="text-xs font-black text-slate-800 leading-tight">{ev.title}</p>
                            <p className="text-[11px] font-semibold text-slate-500 mt-0.5 truncate">{ev.detail}</p>
                          </div>
                          <p className="text-[10px] font-bold text-slate-400 whitespace-nowrap flex-shrink-0 font-mono">
                            {fmtDateTime(ev.date)}
                          </p>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* HISTÓRICO DE PEDIDOS */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <ClipboardList size={13} className="text-[#0A3D73]" />
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73]">Histórico de Pedidos</p>
              <span className="ml-auto text-xs font-black text-slate-400">{totalPedidos} pedidos</span>
            </div>

            {pedidos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-slate-400 gap-3">
                <ClipboardList size={36} className="opacity-20" />
                <p className="text-xs font-black uppercase tracking-widest">Nenhum pedido encontrado</p>
                <p className="text-xs text-slate-400">Os pedidos aparecerão aqui quando forem registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                      <th className="px-3 py-2">Nº Pedido</th>
                      <th className="px-3 py-2">FE</th>
                      <th className="px-3 py-2">Referência</th>
                      <th className="px-3 py-2 text-center">Entrega</th>
                      <th className="px-3 py-2 text-right">Valor</th>
                      <th className="px-3 py-2 text-right">Comissão</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidos.map((p, idx) => {
                      const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.PENDENTE;
                      return (
                        <tr
                          key={p.id || idx}
                          className={`text-xs border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                          <td className="px-3 py-1.5 font-mono font-bold text-[#0A3D73] whitespace-nowrap">
                            {p.numero_fabrica || p.numero_oc || '---'}
                          </td>
                          <td className="px-3 py-1.5 font-mono font-bold text-slate-600 whitespace-nowrap">
                            {p.numero_fe || '---'}
                          </td>
                          <td className="px-3 py-1.5 font-semibold text-slate-700 max-w-[160px] truncate">
                            {p.item_nome || '---'}
                          </td>
                          <td className="px-3 py-1.5 text-center font-semibold text-slate-600 whitespace-nowrap">
                            {fmtDate(p.data_entrega)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-black text-slate-800 whitespace-nowrap">
                            {brl(p.valor_total)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-bold text-slate-600 whitespace-nowrap">
                            {brl(p.comissao_valor)}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <span className={`inline-block px-2 py-0.5 text-[9px] font-black border rounded-full whitespace-nowrap ${cfg.cls}`}>
                              {cfg.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ClienteDetalhe;
